import React, { useState } from 'react';
import { Box, Typography, CircularProgress, useTheme, useMediaQuery } from '@mui/material';

interface LoadingPageProps {
  title?: string;
  subtitle?: string;
  className?: string;
  size?: 'small' | 'medium' | 'large';
  showSpinner?: boolean;
}

// Default loading messages for variety
const DEFAULT_LOADING_MESSAGES = [
  {
    title: 'Stabilizing Orbit...',
    subtitle: "Hold tight—we're syncing systems across the platform.",
  },
  {
    title: 'Punching in Coordinates...',
    subtitle: 'Plotting your next move through the startupverse.',
  },
  {
    title: 'Running Diagnostics...',
    subtitle: "Making sure everything's sharp before we move forward.",
  },
  {
    title: 'Tuning Comms Array...',
    subtitle: 'Establishing a strong signal between you and the mission AI.',
  },
  {
    title: 'Booting the LaunchPad...',
    subtitle: 'Getting your tools, modules, and data ready.',
  },
  {
    title: 'Engaging Core Systems...',
    subtitle: 'Prepping everything behind the scenes for your next action.',
  },
  {
    title: 'Checking Cabin Pressure...',
    subtitle: "Everything's fine. This is just a formality... probably.",
  },
  {
    title: 'Clearing Space Dust...',
    subtitle: 'Tidying things up before your next move.',
  },
  {
    title: 'Spinning Up Subsystems...',
    subtitle: 'Just making sure all modules are awake and alert.',
  },
  {
    title: 'Rendering the Universe...',
    subtitle: "Almost there. You're at the center of it.",
  },
];

const LoadingPage: React.FC<LoadingPageProps> = ({
  title,
  subtitle,
  className = '',
  size = 'medium',
  showSpinner = true,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [currentMessage] = useState(() => {
    // Pick a random message on component mount
    const randomIndex = Math.floor(Math.random() * DEFAULT_LOADING_MESSAGES.length);
    return DEFAULT_LOADING_MESSAGES[randomIndex];
  });

  // Use provided props or fall back to random message
  const displayTitle = title || currentMessage.title;
  const displaySubtitle = subtitle || currentMessage.subtitle;

  // Size configurations
  const sizeConfig = {
    small: {
      spinner: 32,
      titleVariant: 'h6' as const,
      subtitleVariant: 'body2' as const,
      spacing: 2,
    },
    medium: {
      spinner: isMobile ? 48 : 60,
      titleVariant: 'h5' as const,
      subtitleVariant: 'body1' as const,
      spacing: 3,
    },
    large: {
      spinner: isMobile ? 64 : 80,
      titleVariant: 'h4' as const,
      subtitleVariant: 'h6' as const,
      spacing: 4,
    },
  };

  const config = sizeConfig[size];

  return (
    <Box
      className={`min-h-screen w-full flex items-center justify-center ${className}`}
      sx={{
        background: 'linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Animated background elements */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            'radial-gradient(circle at 20% 80%, rgba(36, 137, 249, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(36, 137, 249, 0.1) 0%, transparent 50%)',
          animation: 'pulse 4s ease-in-out infinite',
          '@keyframes pulse': {
            '0%, 100%': {
              opacity: 0.3,
            },
            '50%': {
              opacity: 0.6,
            },
          },
        }}
      />

      {/* Main content */}
      <Box
        className="relative z-10 text-center px-4"
        sx={{
          maxWidth: isMobile ? '90%' : '600px',
        }}
      >
        {/* Spinner */}
        {showSpinner && (
          <Box sx={{ mb: config.spacing }}>
            <CircularProgress
              size={config.spinner}
              sx={{
                color: '#2489f9',
                '& .MuiCircularProgress-circle': {
                  strokeLinecap: 'round',
                },
              }}
            />
          </Box>
        )}

        {/* Title */}
        <Typography
          variant={config.titleVariant}
          className="text-white font-heading font-bold mb-2"
          sx={{
            fontSize: isMobile ? (config.titleVariant === 'h4' ? '1.5rem' : '1.25rem') : undefined,
            lineHeight: 1.2,
          }}
        >
          {displayTitle}
        </Typography>

        {/* Subtitle */}
        <Typography
          variant={config.subtitleVariant}
          className="text-gray-300 font-heading"
          sx={{
            fontSize: isMobile
              ? config.subtitleVariant === 'h6'
                ? '0.875rem'
                : '0.75rem'
              : undefined,
            lineHeight: 1.4,
            opacity: 0.8,
          }}
        >
          {displaySubtitle}
        </Typography>

        {/* Optional loading dots animation */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 2,
            gap: 0.5,
          }}
        >
          {[0, 1, 2].map((index) => (
            <Box
              key={index}
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: '#2489f9',
                animation: 'bounce 1.4s ease-in-out infinite both',
                animationDelay: `${index * 0.16}s`,
                '@keyframes bounce': {
                  '0%, 80%, 100%': {
                    transform: 'scale(0)',
                    opacity: 0.5,
                  },
                  '40%': {
                    transform: 'scale(1)',
                    opacity: 1,
                  },
                },
              }}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default LoadingPage;
